import { useState, useMemo, type ChangeEvent } from 'react';
import './IconPicker.css';

export interface IconPickerProps {
  value: string;
  onChange: (iconClass: string) => void;
}

interface IconCategory {
  label: string;
  icons: string[];
}

const ICON_CATEGORIES: IconCategory[] = [
  {
    label: 'Finance',
    icons: [
      'la-dollar-sign', 'la-euro-sign', 'la-ruble-sign', 'la-wallet', 'la-credit-card',
      'la-chart-line', 'la-chart-bar', 'la-chart-pie', 'la-coins',
    ],
  },
  {
    label: 'Business',
    icons: [
      'la-briefcase', 'la-building', 'la-handshake', 'la-bullhorn', 'la-archive',
      'la-balance-scale', 'la-tasks', 'la-project-diagram',
    ],
  },
  {
    label: 'Analytics',
    icons: ['la-chart-area', 'la-poll', 'la-tachometer-alt'],
  },
  {
    label: 'Status',
    icons: [
      'la-check', 'la-check-circle', 'la-times', 'la-times-circle',
      'la-exclamation-triangle', 'la-info-circle', 'la-question-circle',
    ],
  },
  {
    label: 'Trends',
    icons: [
      'la-arrow-up', 'la-arrow-down', 'la-arrow-right', 'la-arrows-alt-h',
      'la-arrows-alt-v', 'la-sort', 'la-exchange-alt',
    ],
  },
  {
    label: 'Users',
    icons: [
      'la-users', 'la-user', 'la-user-tie', 'la-user-plus', 'la-user-minus',
      'la-user-check', 'la-user-clock', 'la-user-shield',
    ],
  },
  {
    label: 'Time',
    icons: [
      'la-clock', 'la-calendar', 'la-calendar-check', 'la-calendar-times',
      'la-history', 'la-stopwatch', 'la-hourglass',
    ],
  },
];

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [search, setSearch] = useState('');
  const query = search.toLowerCase();

  const filtered = useMemo(() => {
    return ICON_CATEGORIES
      .map((cat) => ({
        ...cat,
        icons: cat.icons.filter(
          (icon) =>
            icon.toLowerCase().includes(query) ||
            cat.label.toLowerCase().includes(query),
        ),
      }))
      .filter((cat) => cat.icons.length > 0);
  }, [query]);

  return (
    <div className="icon-picker">
      <input
        className="icon-picker__search"
        type="text"
        placeholder="Search icons..."
        value={search}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        data-testid="icon-picker-search"
      />
      {filtered.map((cat) => (
        <div key={cat.label}>
          <div className="icon-picker__category">{cat.label}</div>
          <div className="icon-picker__grid">
            {cat.icons.map((icon) => (
              <button
                key={icon}
                type="button"
                className={`icon-picker__icon${
                  value === `las ${icon}` ? ' icon-picker__icon--selected' : ''
                }`}
                data-testid={`icon-picker-${icon}`}
                onClick={() => onChange(`las ${icon}`)}
              >
                <i className={`las ${icon}`} />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
